import { GUID, walkWithQueue } from "@faro-lotv/foundation";
import {
  IElementGenericPointCloudStream,
  isIElementGenericPointCloudStream,
  isValid,
} from "@faro-lotv/ielement-types";
import {
  CachedWorldTransform,
  DEFAULT_TRANSFORM,
} from "@faro-lotv/project-source";
import {
  CaptureTreeEntityRevision,
  isDeletedRevisionStatus,
  RevisionScanEntity,
} from "@faro-lotv/service-wires";
import { Vector3 } from "three";
import { ChildrenMap } from "./revision-entity-selectors";
import { EntityMap } from "./revision-slice";
import { RevisionTransformCache } from "./revision-transform-cache";

/**
 * @param transformCache The cache of entity world transforms.
 * @param entityId The entity to get the position for.
 * @returns The position of the entity with the given ID, as a vector.
 */
export function getEntityPosition(
  transformCache: RevisionTransformCache,
  entityId?: GUID,
): Vector3 {
  const cachedPosition = getEntityTransformCache(
    transformCache,
    entityId,
  ).position;
  return new Vector3().fromArray(cachedPosition);
}

/**
 *
 * @param transformCache The cache of entity world transforms.
 * @param entityId The entity to get the cached transform for.
 * @returns The cached transform for the entity. Defaults to the identity transform.
 */
export function getEntityTransformCache(
  transformCache: RevisionTransformCache,
  entityId?: GUID,
): CachedWorldTransform {
  return entityId
    ? (transformCache[entityId] ?? DEFAULT_TRANSFORM)
    : DEFAULT_TRANSFORM;
}

/**
 * @param entity The revision entity to check.
 * @returns true, if the entity is deleted on the revision, else false.
 */
export function isEntityDeletedOnRevision(
  entity: CaptureTreeEntityRevision,
): boolean {
  return isDeletedRevisionStatus(entity.status);
}

/**
 * @param childrenMap a cached map of entities to their children
 * @param entityId The ID of the entity to get the descendant IDs for.
 * @returns The IDs of all descendants, including the entity itself.
 */
export function getDescendantIds(
  childrenMap: ChildrenMap,
  entityId: GUID,
): GUID[] {
  const descendantIds = [entityId];

  walkWithQueue([entityId], (current, append) => {
    const children = childrenMap[current];
    const childIds = children?.map((child) => child.id);

    if (childIds) {
      descendantIds.push(...childIds);
      append(...childIds);
    }
  });

  return descendantIds;
}

/**
 * @param entityMap All loaded entities in the revision.
 * @param entityId The ID of the entity to get the ancestor IDs for.
 * @returns The IDs of all ancestors, including the entity itself.
 */
export function getAncestorIds(entityMap: EntityMap, entityId: GUID): GUID[] {
  const ancestorIds = [];
  let currentId: GUID = entityId;

  while (currentId) {
    ancestorIds.push(currentId);

    const entity = entityMap[currentId];
    if (!entity || !entity.parentId) break;

    currentId = entity.parentId;
  }

  return ancestorIds;
}

/**
 * Gets the point cloud stream for a given scan entity.
 *
 * @param iElements - Array of point cloud stream elements to search through
 * @param scanEntityId - ID of the scan entity to find the point cloud stream for
 * @returns The point cloud stream element that belongs to the scan entity, or undefined if none found
 *
 * ASSUMPTIONS:
 * - The scan entity of the revision has the same ID as the data set IElement in the Capture Tree
 * - The PointCloudStream element is a direct child of that data set IElement
 */
export function getPointCloudStreamForScanEntity(
  iElements: IElementGenericPointCloudStream[],
  scanEntityId: GUID,
): IElementGenericPointCloudStream | undefined {
  if (!scanEntityId) return undefined;

  return iElements
    .filter(isIElementGenericPointCloudStream)
    .find((iElement) => iElement.parentId === scanEntityId);
}

/**
 * Returns an array of GUIDs representing point cloud stream IDs for the given scan entities.
 *
 * @param scanEntities Array of scan entities to process.
 * @param iElements Array of point cloud stream elements to search through.
 * @returns An array of GUIDs representing point cloud stream IDs for the given entities.
 */
export function getPointCloudStreamIdsForEntities(
  scanEntities: RevisionScanEntity[],
  iElements: IElementGenericPointCloudStream[],
): GUID[] {
  return scanEntities
    .map((scan) => getPointCloudStreamForScanEntity(iElements, scan.id))
    .filter(isValid)
    .map((pcs) => pcs.id);
}
